import { usePhoneFormatter } from '@ltvco/refresh-lib/utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { useState } from 'react';

import {
  CardHeaderRow,
  CardContainer,
  ReportTitle,
  ReportTypeChip,
  SeeFullReportCTA,
  ViewReportButton,
} from '../../components';
import {
  getFraudRiskConfig,
  type FraudRiskLevel,
  type StatusSeverity,
} from '../utils';

import {
  RiskChip,
  RiskChipLabel,
  RiskDescription,
  RiskExpansion,
  RiskSection,
} from './components';
import {
  FraudScanSection,
  LineDetailsSection,
  LinkedRecordsSection,
  PotentialOwnersSection,
} from './sections';

import type { PhoneReportData } from 'components/DeepSearch/types';

interface PhoneReportPreviewCardProps {
  data: PhoneReportData;
  onViewReport?: () => void;
  onSendMessage?: (content: string, displayContent?: string) => void;
}

const RiskChipIcon = ({ severity }: { severity: StatusSeverity }) => {
  const sx = { fontSize: 16 };
  if (severity === 'success') {
    return <CheckCircleIcon sx={sx} />;
  }
  if (severity === 'warning') {
    return <WarningRoundedIcon sx={sx} />;
  }
  return <ErrorIcon sx={sx} />;
};

export const PhoneReportPreviewCard = ({
  data,
  onViewReport,
  onSendMessage,
}: PhoneReportPreviewCardProps) => {
  const [riskOpen, setRiskOpen] = useState(false);

  const phoneLine = data.phoneLine;
  const rawNumber = phoneLine?.phoneNumber ?? '';
  const formattedNumber = usePhoneFormatter(rawNumber, true);

  const rawLevel = (data.fraud?.riskLevel?.trim().toLowerCase() ??
    'unknown') as FraudRiskLevel;
  const risk = getFraudRiskConfig(rawLevel);

  return (
    <CardContainer>
      <CardHeaderRow sx={{ mb: 2 }}>
        <PhoneIcon sx={{ fontSize: 24 }} />
        <ReportTitle m={0}>
          {formattedNumber || rawNumber || 'Phone Report'}
        </ReportTitle>
        {onViewReport && <ViewReportButton onClick={onViewReport} />}
      </CardHeaderRow>

      {phoneLine?.lineType && (
        <ReportTypeChip>{phoneLine.lineType}</ReportTypeChip>
      )}

      {risk && (
        <RiskSection>
          <RiskChip
            type="button"
            severity={risk.severity}
            aria-expanded={riskOpen}
            onClick={() => setRiskOpen((o) => !o)}
          >
            <RiskChipIcon severity={risk.severity} />
            <RiskChipLabel variant="body1">{risk.label}</RiskChipLabel>
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 16,
                transition: 'transform 0.2s',
                transform: riskOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </RiskChip>
          <RiskExpansion in={riskOpen}>
            <RiskDescription variant="body1">
              {risk.description}
            </RiskDescription>
          </RiskExpansion>
        </RiskSection>
      )}

      <FraudScanSection
        fraud={data.fraud}
        spam={data.spam}
        phoneLine={phoneLine}
      />

      {phoneLine && <LineDetailsSection phoneLine={phoneLine} />}

      {data.potentialOwner && (
        <PotentialOwnersSection
          owner={data.potentialOwner}
          onSendMessage={onSendMessage}
        />
      )}

      <LinkedRecordsSection
        addresses={data.potentialAddresses}
        emails={data.potentialEmails}
        phones={data.potentialPhoneNumbers}
        currentNumber={rawNumber}
        jobs={data.jobs}
        education={data.education}
        onSendMessage={onSendMessage}
      />
      {onViewReport && <SeeFullReportCTA onClick={onViewReport} />}
    </CardContainer>
  );
};
